import React, { useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import 'react-toastify/dist/ReactToastify.css';
import store from './store/store';
import { buildTheme } from './theme/theme';
import AppRoutes from './routes/AppRoutes';
import ErrorBoundary from './components/ErrorBoundary';
import { loadUser } from './store/slices/authSlice';
import { FontScaleProvider } from './contexts/FontScaleContext';
import { ColorModeProvider, useColorMode } from './contexts/ColorModeContext';
import AppToasts from './components/AppToasts';

function AppContent() {
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      store.dispatch(loadUser());
    }
  }, []);

  return <AppRoutes />;
}

/**
 * Everything below the colour mode. Split out because the theme is built from
 * the mode, which means reading the context — and that can only happen inside
 * the provider.
 */
function ThemedApp() {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </BrowserRouter>
      <AppToasts />
    </ThemeProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      {/* Pinned to light while the light/dark toggle is hidden from the app
          bar. Drop `forced` and restore <ThemeModeToggle /> in
          DashboardLayout to bring the feature back — nothing else changes. */}
      <ColorModeProvider forced="light">
        <FontScaleProvider>
          <ThemedApp />
        </FontScaleProvider>
      </ColorModeProvider>
    </Provider>
  );
}

export default App;
